import { useState, useEffect } from "react";
import { useDotLegalQueryArray, useDotLegalQueryWithKey, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { post } from "@common/api/apiShared";
import { BestPracticeDocumentTemplateViewModel } from "../../../bestPracticeDocumentTemplate/BestPracticeDocumentTemplates.types";
import { BestPracticeCheckCompareModel, BestPracticeCheckStatus, BestPracticeCheckViewModel } from "./BestPracticeCheckDialog.types";

interface UseBestPracticeCheckDialogProps {
    documentId: string;
    documentVersionId?: string;
    onCheckComplete: (result: BestPracticeCheckViewModel) => void;
}

export function useBestPracticeCheckDialog(props: UseBestPracticeCheckDialogProps) {
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [pollingDocumentVersionId, setPollingDocumentVersionId] = useState<string | null>(null);

    const { isLoading: isLoadingTemplates, data: templates } = useDotLegalQueryWithKey<Array<BestPracticeDocumentTemplateViewModel>>(
        "best-practice-document-templates",
        "/best-practice-document-templates"
    );

    const compareMutation = useDotLegalMutation(CompareDocumentAPI);

    const { data: pollingResult } = useDotLegalQueryArray<BestPracticeCheckViewModel>(
        ["best-practice-check-poll", pollingDocumentVersionId],
        `/document-version-best-practice-check/${pollingDocumentVersionId}/version`,
        {
            enabled: !!pollingDocumentVersionId,
            refetchInterval: (query) => {
                const status = query.state.data?.status;
                if (status === BestPracticeCheckStatus.Completed || status === BestPracticeCheckStatus.Failed) {
                    return false;
                }
                return 2000;
            },
        }
    );

    useEffect(() => {
        if (!pollingResult) return;

        if (pollingResult.status === BestPracticeCheckStatus.Completed) {
            setPollingDocumentVersionId(null);
            props.onCheckComplete(pollingResult);
        }

        if (pollingResult.status === BestPracticeCheckStatus.Failed) {
            setPollingDocumentVersionId(null);
            compareMutation.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollingResult?.status]);

    const onOkClick = () => {
        if (!selectedTemplateId) return;

        compareMutation.mutate(
            {
                documentId: props.documentId,
                documentVersionId: props.documentVersionId,
                bestPracticeDocumentTemplateId: selectedTemplateId,
            },
            {
                onSuccess: (response: any) => {
                    const result = response.value();
                    setPollingDocumentVersionId(result.documentVersionId);
                },
            }
        );
    };

    const templateOptions = templates?.map((template) => ({
        id: template.id,
        name: template.name,
    }));

    const isComparing = compareMutation.isLoading || !!pollingDocumentVersionId;

    return {
        selectedTemplateId,
        setSelectedTemplateId,
        isLoadingTemplates,
        templateOptions,
        onOkClick,
        isComparing,
    };

    function CompareDocumentAPI(model: BestPracticeCheckCompareModel) {
        return post<BestPracticeCheckViewModel>("/document-version-best-practice-check/compare", model);
    }
}

export function useBestPracticeDialogsHook() {
    const [bestPracticeCheckDocumentToQualityAssurance, setBestPracticeCheckDocumentToQualityAssurance] = useState<
        { documentId: string; documentVersionId?: string } | undefined
    >(undefined);
    const [bestPracticeCheckDocument, setBestPracticeCheckDocument] = useState<{ documentId: string; documentVersionId?: string } | undefined>(undefined);
    const [bestPracticeCheckInitialResult, setBestPracticeCheckInitialResult] = useState<BestPracticeCheckViewModel | undefined>(undefined);

    const onCheckComplete = (result: BestPracticeCheckViewModel) => {
        setBestPracticeCheckDocument(
            bestPracticeCheckDocumentToQualityAssurance
                ? {
                      documentId: bestPracticeCheckDocumentToQualityAssurance.documentId,
                      documentVersionId: bestPracticeCheckDocumentToQualityAssurance.documentVersionId,
                  }
                : undefined
        );
        setBestPracticeCheckInitialResult(result);
        setBestPracticeCheckDocumentToQualityAssurance(undefined);
    };

    return {
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
    };
}
